import { FormType } from "@/components/Form/types";
import { CASE_TYPE } from "@/core/constants/case.constants";
import type { AppDispatch } from "@/core/app/store";
import FORM_CONSTANT from "./form.constant";
import { warmFormByCaseId } from "./formThunkApi";


export const getFormsByType=(forms: SmartRxForm[],  type: FormType) => {
    return forms.filter((item: SmartRxForm) =>  item.type === type) || [];
}

export const getFormByName=(forms,  type: FormType, name: string) => {
    return forms.find((item: SmartRxForm) =>  item.type === type && item.name.toLowerCase() === name?.toLowerCase()) || {};
}

/**
 * Like getFormByName(...).id, but throws instead of returning undefined so an
 * unresolved form never reaches a URL as "/forms/undefined/...".
 */
export const requireFormId = (forms: SmartRxForm[], type: FormType, name: string): string => {
    const formId = getFormByName(forms, type, name).id
    if (!formId) throw new Error(`Form not found: ${type}/${name}`)
    return formId
}

export const getFormByID=(forms,  type: FormType, id: string) => {
    return forms.find((item: SmartRxForm) =>  item.type === type && item.id.toLowerCase() === id.toLowerCase()) || {};
}

/**
 * Resolves whether the current case should show the Prescription or Retainer
 * uassist form, from caseRAtype first and caseDetail.udesign_json as a fallback.
 * Returns null when it can't be determined yet.
 */
export const resolveUassistFormName = (
  caseRAtype: string | undefined,
  caseDetail: { udesign_json?: string } | undefined,
): string | null => {
  if (caseRAtype) {
    // Self Plan cases don't use a uAssist form - nothing to warm.
    if (caseRAtype === CASE_TYPE.SELFPLAN) return null
    return caseRAtype === CASE_TYPE.RETAINER
      ? FORM_CONSTANT.RETAINER
      : FORM_CONSTANT.PRESCRIPTION
  }
  if (!caseDetail?.udesign_json) return null
  let parsed: { UpperType?: string; LowerType?: string } | null = null
  try {
    parsed = JSON.parse(caseDetail.udesign_json)
  } catch {
    return null
  }
  // Guards against valid-but-useless JSON (e.g. the literal string "null" or "123"),
  // not just malformed JSON that JSON.parse itself throws on.
  if (!parsed || typeof parsed !== "object") return null
  const { UpperType, LowerType } = parsed
  if (LowerType === "A" || UpperType === "A") return FORM_CONSTANT.PRESCRIPTION
  if (
    (UpperType === "R" || LowerType === "R") &&
    UpperType !== "A" &&
    LowerType !== "A"
  ) {
    return FORM_CONSTANT.RETAINER
  }
  return null
}

/**
 * Builds the identity key used to match a warmed form-fetch cache entry
 * against what Form.tsx actually needs when it mounts.
 */
export const buildFormCacheKey = ({
  formId,
  caseId,
  patientId,
  uassistRound,
  getSavedData,
}: {
  formId?: string
  caseId?: string
  patientId?: string
  uassistRound?: number
  getSavedData?: boolean
}): string =>
  `${formId}:${caseId}:${patientId}:${uassistRound}:${getSavedData}`

/**
 * Resolves the case's current uassist form and dispatches warmFormByCaseId for it,
 * skipping the dispatch if the cache already has a matching pending/succeeded entry.
 * Shared by CaseType.tsx and Scans.tsx, the two places that warm the Forms page ahead
 * of the user reaching it.
 */
export const warmUassistForm = ({
  dispatch,
  formTemplateList,
  formWarmCache,
  formName,
  caseId,
  patientId,
  uassistRound = 0,
}: {
  dispatch: AppDispatch
  formTemplateList: SmartRxForm[]
  formWarmCache: { key: string | null; status: string }
  formName: string | null
  caseId: string
  patientId: string
  uassistRound?: number
}): void => {
  if (!formName || formTemplateList.length === 0) return
  const formId = getFormByName(formTemplateList, FormType.UASSIST, formName).id
  if (!formId) return

  const fetchParams = {
    formId,
    caseId,
    getSavedData: true,
    patientId,
    formType: FormType.UASSIST,
    uassistRound,
  }
  const key = buildFormCacheKey(fetchParams)
  if (
    (formWarmCache.status === "pending" || formWarmCache.status === "succeeded") &&
    formWarmCache.key === key
  ) {
    return
  }
  dispatch(warmFormByCaseId(fetchParams))
}